import { Tabs, Badge, Box, Button, Flex, Heading, Text } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TableComponent from '../components/ui-components/TableComponent';
import AddReminder from '../components/ui-components/CreateMedication';
import ViewMedication from '../components/ui-components/ViewMedication';
import axiosInstance from '../config/axiosConfig';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { toast } from 'react-toastify';
import { formatCalendarDate } from '../utils/helpers';

const MedicationPage = () => {
  const location = new URLSearchParams(useLocation().search);
  const patientToken = location.get('patientToken');
  const bedId = location.get('bedId');
  const roleId = localStorage.getItem('roleId');
  const canManageMedication = roleId === '3';
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Medication');

  const fetchReminders = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axiosInstance.get('/MedicActionReminder/get',{
      params: {
        patientToken: patientToken,  
        bedId: bedId                    
      },
    });
      const reminderList = response.data.result || [];
      console.log('reminderList', reminderList);
      setReminders(reminderList);
      setError(null);
    } catch (err) {
      setError('Failed to load reminders');
      console.error(err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (patientToken) {
      fetchReminders();
    } else {
      setError('No patient token provided');
      setLoading(false);
    }
  }, [patientToken, bedId]);

  const handleReminderSaved = async (change = null) => {
    if (change?.deleted) {
      setReminders((items) => items.filter((item) => item.reminder.token !== change.token));
    } else if (typeof change?.isActive === 'boolean') {
      setReminders((items) =>
        items.map((item) =>
          item.reminder.token === change.token
            ? { ...item, reminder: { ...item.reminder, isActive: change.isActive } }
            : item
        )
      );
    }

    await fetchReminders(false);
  };

  const showToast = (message, type = 'success') => {
    toast(message, { type });
  };

  const formatDate = (dateString) => {
    return formatCalendarDate(dateString);
  };

  // Format time to 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    // Check if the time is already in 12-hour format
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to format days display
  const formatDays = (repetition) => {
    if (!repetition) return '-';
    
    const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayProps = ['isSunday', 'isMonday', 'isTuesday', 'isWednesday', 'isThursday', 'isFriday', 'isSaturday'];
    
    const selectedDays = dayProps
      .map((prop, index) => repetition[prop] ? dayAbbreviations[index] : null)
      .filter(day => day !== null);
    
    if (selectedDays.length === 7) {
      return <Badge color="blue">Every day</Badge>;
    } else if (selectedDays.length === 0) {
      return <Badge color="gray">No days selected</Badge>;
    } else if (JSON.stringify(selectedDays) === JSON.stringify(['Mon', 'Tue', 'Wed', 'Thu', 'Fri'])) {
      return <Badge color="green">Weekdays</Badge>;
    } else if (JSON.stringify(selectedDays) === JSON.stringify(['Sat', 'Sun'])) {
      return <Badge color="orange">Weekends</Badge>;
    }
    
    return (
      <Flex gap="1" wrap="wrap">
        {selectedDays.map((day, index) => (
          <Badge key={index} color="blue" variant="soft" size="1">
            {day}
          </Badge>
        ))}
      </Flex>
    );
  };

  // Add serialNumber property to each reminder item
  const addSerialNumbers = (items) => {
    return items.map((item, index) => ({
      ...item,
      serialNumber: index + 1
    }));
  };

  // Apply serial numbers to filtered lists
  const serializedMedications = addSerialNumbers(
    reminders.filter(r => r.reminder.type === 'Medication' || r.reminder.type === 1 || r.reminder.type === 0 || r.reminder.type === 'Medic')
  );
  
  const serializedActions = addSerialNumbers(
    reminders.filter(r => r.reminder.type === 'Action' || r.reminder.type === 2)
  );

  const columns = [
    {
      title: '#',
      key: 'serialNumber',
      render: (value) => <Text size="2">{value}</Text>,
    },
    {
      title: activeTab === 'Medication' ? 'Medication' : 'Task',
      key: 'details',
      render: (_, row) => (
        <Text size="2">{row.details?.map((d) => d.medication).join(', ') || '-'}</Text>
      ),
    },
    {
      title: 'Dosage',
      key: 'dosage',
      render: (_, row) => activeTab === 'Medication' ? (
        <Text size="2">
          {row.details?.map((d) => d.dosage || '-').join(', ') || '-'}
        </Text>
      ) : null,
      hidden: activeTab !== 'Medication', // Hide for Action tab
    },
    {
      title: 'Time',
      key: 'reminder.time',
      render: (_, row) => <Text size="2">{formatTime(row.reminder.time)}</Text>,
    },
    {
      title: 'Days',
      key: 'repetition',
      render: (_, row) => formatDays(row.repetition),
    },
    {
      title: 'From',
      key: 'reminder.from',
      render: (_, row) => <Text size="2">{formatDate(row.reminder.from)}</Text>,
    },
    {
      title: 'To',
      key: 'reminder.to',
      render: (_, row) => <Text size="2">{formatDate(row.reminder.to)}</Text>,
    },
    {
      title: 'Status',
      key: 'reminder.isActive',
      render: (_, row) => (
        row.reminder.isActive ? (
          <Badge color="green">
            <MdOutlineVerifiedUser color="green" /> Active
          </Badge>
        ) : (
          <Badge color="tomato">
            <MdOutlineVerifiedUser color="red" /> Inactive
          </Badge>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Flex gap="2" align="center">
          <ViewMedication
            initialValues={row}
            onReminderUpdated={handleReminderSaved}
            showToast={showToast}
            canManage={canManageMedication}
          />
        </Flex>
      ),
    },
  ];

  // Filter columns based on activeTab
  const visibleColumns = columns.filter(col => !col.hidden);

  if (loading) {
    return (
      <Flex justify="center">
        <Text>Loading...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center">
        <Text>{error}</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="3">
      <Flex justify="between">
        <Heading className="text-gray-900">Reminder List</Heading>
        {canManageMedication && (
          <AddReminder
            onReminderSaved={handleReminderSaved}
            showToast={showToast}
          />
        )}
      </Flex>
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Trigger value="Medication">Medications</Tabs.Trigger>
          <Tabs.Trigger value="Action">Tasks</Tabs.Trigger>
        </Tabs.List>
        <Box pt="3">
          <Tabs.Content value="Medication">
            <TableComponent
              dataStructure={serializedMedications}
              columns={visibleColumns}
            />
          </Tabs.Content>
          <Tabs.Content value="Action">
            <TableComponent
              dataStructure={serializedActions}
              columns={visibleColumns}
            />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Flex>
  );
};

export default MedicationPage;
